/**
 * Where you are on the road, per part.
 *
 * Derived from the *filtered* set rather than the whole catalog, deliberately.
 * Someone on the Bare Minimum track has a shorter road, and telling them they
 * are "3 of 12" through a part they can only see 5 of would be describing a
 * road they are not walking. The counts always match what is on screen.
 */

import type { Entry, EntryId, Section } from "@schema/catalog.ts";

export interface SectionProgress {
  section: Section;
  entries: Entry[];
  /** Entries in this part before filtering, so a header can own up to hiding some. */
  total: number;
  watched: number;
  /** 0–1 across the visible entries; 1 for an empty part, which reads as done. */
  fraction: number;
  done: boolean;
}

export function sectionProgress(
  groups: { section: Section; entries: Entry[]; total: number }[],
  watched: Record<EntryId, unknown>,
): SectionProgress[] {
  return groups.map(({ section, entries, total }) => {
    const count = entries.reduce((n, e) => n + (e.id in watched ? 1 : 0), 0);
    return {
      section,
      entries,
      total,
      watched: count,
      fraction: entries.length === 0 ? 1 : count / entries.length,
      done: count === entries.length,
    };
  });
}

/**
 * The next stop you have not watched, in road order.
 *
 * Returns null when everything visible is watched — the caller should then say
 * nothing rather than offer a jump to nowhere.
 */
export function firstUnwatched(entries: Entry[], watched: Record<EntryId, unknown>): Entry | null {
  return entries.find((e) => !(e.id in watched)) ?? null;
}
